import React, { useEffect, useMemo, useRef } from 'react';

import { ImageProps } from 'next/image';
import Link from 'next/link';

import analytics from '@repo/analytics';
import { TransformedUseCase } from '@repo/editor/src/types';
import { withEventScope } from '@repo/event-processor';
import ArrowIcon from '@repo/ui/components/Icon/ArrowIcon';
import { P2 } from '@repo/ui/components/Typography';
import { BREAKPOINTS } from '@repo/ui/queries';
import { themeStyle } from '@repo/ui/theming';

import { buildImageProxyUrlByObjectName } from 'components/AiTools/utils';
import useListScroll from 'components/hooks/useListScroll';
import { USECASES_USECASE } from 'global/analyticsEvents';

import { filterAndSortUseCases, getMergedUseCases } from '../utils';

import {
  CardsWrapper,
  List,
  CardItem,
  CardPreview,
  ScrollButton,
  cardPreviewDimensions,
  CardImage,
  OverlayText,
  UseCaseText,
} from './styles';
import { UseCaseSectionProps } from './types';
import { buildUseCaseUrl } from './utils';

const UseCaseSection: React.FC<UseCaseSectionProps> = ({
  allUseCases,
  scale = 'regular',
  currentSelection,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const activeCardRef = useRef<HTMLAnchorElement>(null);

  const {
    canScrollLeft,
    canScrollRight,
    updateScrollState,
    scrollLeft,
    scrollRight,
  } = useListScroll(listRef, 180);

  const {
    useCaseSlug: currentUseCaseSlug,
    categorySlug: currentCategorySlug,
    subCategorySlug: currentSubCategorySlug,
  } = currentSelection;

  const useCases: TransformedUseCase[] = useMemo(() => {
    const mergedUseCases = getMergedUseCases(
      allUseCases,
      currentCategorySlug,
      currentSubCategorySlug
    );

    const filteredUseCases = filterAndSortUseCases(mergedUseCases);

    return [...filteredUseCases];
  }, [allUseCases, currentCategorySlug, currentSubCategorySlug]);

  useEffect(() => {
    if (useCases) {
      if (activeCardRef.current) {
        activeCardRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      updateScrollState();
    }
  }, [updateScrollState, useCases, activeCardRef]);

  const handleUseCaseClick = ({
    name,
    slug,
  }: Pick<TransformedUseCase, 'name' | 'slug'>): void => {
    void analytics.track(USECASES_USECASE, { name, slug });
  };

  const createCard = (useCase: TransformedUseCase) => {
    const isActive = useCase.slug === currentUseCaseSlug;
    const url = buildUseCaseUrl(
      currentSelection,
      isActive ? undefined : useCase
    );
    const getCoverImageProps = (
      useCase: TransformedUseCase,
      width?: number,
      height?: number
    ): Pick<ImageProps, 'src'> | Pick<ImageProps, 'src' | 'loader'> => {
      const preview = useCase.imagePaths?.categoryPageImage;
      if (!preview) {
        return { src: '/images/category/all-new.png' };
      }

      return {
        src: buildImageProxyUrlByObjectName(preview, width, height),
        loader: ({ width }) =>
          buildImageProxyUrlByObjectName(preview, width, 0),
      };
    };

    return (
      <Link
        key={useCase.slug}
        ref={isActive ? activeCardRef : null}
        data-testid={`usecases-useCase--${useCase.slug}`}
        href={url}
        suppressHydrationWarning={true} // remove after completely move templates feed to app router
        onClick={(): void => handleUseCaseClick(useCase)}
      >
        <CardItem
          isActive={isActive}
          scale={scale}
          suppressHydrationWarning={true} // remove after completely move templates feed to app router
        >
          <CardPreview scale={scale}>
            <CardImage
              fill
              priority
              alt={useCase.slug}
              sizes={`(min-width: ${
                BREAKPOINTS.tabletMin
              }px) ${cardPreviewDimensions({ scale })}, 90px`}
              {...getCoverImageProps(useCase)}
            />
            <OverlayText>
              <P2 color={themeStyle.backgroundAlt}>
                <b>See templates</b>
              </P2>
            </OverlayText>
          </CardPreview>
          <UseCaseText>{useCase.name}</UseCaseText>
        </CardItem>
      </Link>
    );
  };

  return (
    <CardsWrapper>
      <List ref={listRef} scale={scale} onScroll={updateScrollState}>
        {useCases?.map(createCard)}
      </List>
      {canScrollLeft && (
        <ScrollButton align="left" scale={scale} onClick={scrollLeft}>
          <i style={{ transform: 'rotate(180deg)', marginRight: '3px' }}>
            <ArrowIcon
              height="14px"
              theme={{ color: themeStyle.varInkMedium }}
            />
          </i>
        </ScrollButton>
      )}
      {canScrollRight && (
        <ScrollButton align="right" scale={scale} onClick={scrollRight}>
          <i style={{ marginLeft: '3px' }}>
            <ArrowIcon
              height="14px"
              theme={{ color: themeStyle.varInkMedium }}
            />
          </i>
        </ScrollButton>
      )}
    </CardsWrapper>
  );
};

export default withEventScope(UseCaseSection, { feature: 'useCaseSection' });
